/* Sticky mobile add-to-cart bar controller for Dawn.
   - Watches the main product form's submit button via IntersectionObserver
     and reveals the sticky bar when that button is offscreen.
   - Subscribes to Dawn's PUB_SUB_EVENTS.variantChange so variant ID, price,
     and button state stay in sync with the in-page variant picker.
   - On tap, POSTs to /cart/add.js. If cart drawer is enabled, refreshes the
     drawer; otherwise navigates to /cart. */
(function () {
  class StickyAtcBar extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('[data-sticky-atc-button]');
      this.buttonText = this.querySelector('[data-sticky-atc-text]');
      this.priceEl = this.querySelector('[data-price]');
      this.cartType = this.dataset.cartType;
      this._onClick = this._onClick.bind(this);
    }

    connectedCallback() {
      // Wait a tick so Dawn's product-form has registered.
      requestAnimationFrame(() => this._init());
    }

    _init() {
      this.mainAtc =
        document.querySelector('[id^="ProductSubmitButton-"]') ||
        document.querySelector('.product-form__submit');
      if (!this.mainAtc) return;

      this._setupObserver();
      this._subscribeVariantChanges();
      this.button.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      if (this._unsubscribe) this._unsubscribe();
      if (this._io) this._io.disconnect();
      this.button.removeEventListener('click', this._onClick);
    }

    _setupObserver() {
      this._io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Show the sticky bar only when the main ATC has scrolled
            // ABOVE the viewport — not when it's not yet been scrolled to.
            const aboveViewport =
              !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
            this.dataset.visible = aboveViewport ? 'true' : 'false';
            this.setAttribute('aria-hidden', aboveViewport ? 'false' : 'true');
          });
        },
        { threshold: 0, rootMargin: '0px 0px 0px 0px' }
      );
      this._io.observe(this.mainAtc);
    }

    _subscribeVariantChanges() {
      // Dawn's pubsub.js exposes subscribe() and PUB_SUB_EVENTS on window.
      if (
        typeof window.subscribe !== 'function' ||
        typeof window.PUB_SUB_EVENTS === 'undefined'
      ) {
        return;
      }
      this._unsubscribe = window.subscribe(
        window.PUB_SUB_EVENTS.variantChange,
        (event) => {
          const variant = event && event.data && event.data.variant;
          if (!variant) return;
          this.button.dataset.variantId = variant.id;
          this.button.disabled = !variant.available;
          if (this.buttonText) {
            const strings = window.variantStrings || {};
            this.buttonText.textContent = variant.available
              ? strings.addToCart || 'Add to cart'
              : strings.soldOut || 'Sold out';
          }
          if (this.priceEl && typeof variant.price !== 'undefined') {
            this._renderPrice(variant.price, variant.compare_at_price);
          }
        }
      );
    }

    _renderPrice(price, compareAt) {
      const fmt = (cents) =>
        '$' + (Number(cents) / 100).toFixed(2);
      let html = '';
      if (compareAt && compareAt > price) {
        html += '<s class="price__compare">' + fmt(compareAt) + '</s>';
      }
      html += '<span class="price__regular">' + fmt(price) + '</span>';
      this.priceEl.innerHTML = html;
    }

    async _onClick(e) {
      e.preventDefault();
      if (this.button.disabled) return;
      const variantId = this.button.dataset.variantId;
      if (!variantId) return;

      this.button.dataset.loading = 'true';
      try {
        const body = new FormData();
        body.append('id', variantId);
        body.append('quantity', '1');

        // If cart drawer is enabled, request its sections in the same call
        // so the drawer can refresh from the response.
        if (this.cartType === 'drawer') {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
            const sections = cartDrawer
              .getSectionsToRender()
              .map((s) => s.section);
            body.append('sections', sections.join(','));
            body.append('sections_url', window.location.pathname);
          }
        }

        const res = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.description || 'Could not add to cart');
        }

        // Notify Dawn so cart count / drawer / other listeners update.
        if (
          typeof window.publish === 'function' &&
          window.PUB_SUB_EVENTS &&
          window.PUB_SUB_EVENTS.cartUpdate
        ) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'sticky-atc-mobile',
            productVariantId: variantId,
            cartData: data
          });
        }

        if (this.cartType === 'drawer') {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(data);
          } else {
            window.location = window.routes.cart_url;
          }
        } else {
          window.location = window.routes.cart_url;
        }

        this.button.dataset.success = 'true';
        setTimeout(() => {
          this.button.dataset.success = 'false';
        }, 2000);
      } catch (err) {
        // Surface the error to the customer using the main product form's
        // alert region, if available.
        const errorRegion = document.querySelector(
          '.product-form__error-message-wrapper'
        );
        const messageEl = document.querySelector(
          '.product-form__error-message'
        );
        if (errorRegion && messageEl) {
          messageEl.textContent = err.message || 'Could not add to cart';
          errorRegion.hidden = false;
        }
        console.error('[sticky-atc] add failed:', err);
      } finally {
        this.button.dataset.loading = 'false';
      }
    }
  }

  if (!customElements.get('sticky-atc-bar')) {
    customElements.define('sticky-atc-bar', StickyAtcBar);
  }
})();
